import { Component, Output, EventEmitter, Input, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SchemaService, SchemaResponse } from '../../core/service/schema.service';
import { ToastService } from '../../toast/toast.service';
import { OnboardingService } from '../../core/service/onboarding.service';
import { TutorialService } from '../../core/service/tutorial.service';
import { OnboardingModalComponent } from '../onboarding-modal/onboarding-modal.component';
import { SchemaSelectionModalComponent } from '../schema-selection-modal/schema-selection-modal.component';

@Component({
  selector: 'app-empty-state',
  standalone: true,
  imports: [CommonModule, OnboardingModalComponent, SchemaSelectionModalComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './empty-state.component.html',
  styleUrls: ['./empty-state.component.scss']
})
export class EmptyStateComponent implements OnInit, OnDestroy {
  @Input() activeTable: string = '';
  @Input() valid_files: string = '';
  @Input() valid_MIME_types: string[] = [];
  @Input() valid_extensions: string[] = [];
  @Input() uploadFileLoading: boolean = false;
  @Input() workflowType: 'semi' | 'manual' | '' = 'semi';
  @Input() isSidebarOpen: boolean = false;
  @Input() isGeneratingSchema: boolean = false;

  @Output() fileDropped = new EventEmitter<{event: any, isDrop: boolean, dataspaceCreated: boolean, workspaceName?: string, selectedTemplate?: SchemaResponse}>();
  @Output() workflowSelected = new EventEmitter<'semi' | 'manual'>();
  @Output() templateSelected = new EventEmitter<SchemaResponse>();

  structureOptionsContainer: HTMLElement | null = null;

  availableTemplates: SchemaResponse[] = [];
  showAllTemplates = false;
  templateLoadingError = false;
  selectedTemplateId: string | null = null;
  selectedTemplate: SchemaResponse | null = null;
  isHelpMenuOpen = false;
  isDragOver = false;
  showSchemaSelectionModal = false;
  pendingFiles: { files: File[], isDrop: boolean, dataspaceCreated: boolean } | null = null;
  private subscriptions = new Subscription();

  constructor(
    private schemaService: SchemaService,
    private toastService: ToastService,
    private onboardingService: OnboardingService,
    private tutorialService: TutorialService
  ) {}

  ngOnInit() {
    this.loadAvailableTemplates();
    this.subscriptions.add(
      this.tutorialService.checkUserDataspaces().subscribe((hasDataspaces) => {
        this.onboardingService.initializeOnboardingForNewUsers(hasDataspaces);
      })
    );
  }

  private loadAvailableTemplates() {
    this.schemaService.getAllSchemas().subscribe({
      next: (templates) => {
        console.log('Loaded templates:', templates);
        this.availableTemplates = templates;
        this.templateLoadingError = false;
      },
      error: (error) => {
        console.error('Failed to load templates:', error);
        this.templateLoadingError = true;
        this.availableTemplates = [];
      }
    });
  }

  onSchemaSelectionMade(selection: { workflowType: 'semi' | 'manual', workspaceName?: string, selectedTemplate?: SchemaResponse }) {
    this.showSchemaSelectionModal = false;
    
    this.workflowType = selection.workflowType;
    this.workflowSelected.emit(selection.workflowType);
    
    if (selection.selectedTemplate) {
      this.selectedTemplateId = selection.selectedTemplate.schema_id;
      this.selectedTemplate = selection.selectedTemplate;
    } else {
      this.selectedTemplateId = null;
      this.selectedTemplate = null;
    }
    
    if (this.pendingFiles) {
      this.fileDropped.emit({
        event: this.pendingFiles.files,
        isDrop: this.pendingFiles.isDrop,
        dataspaceCreated: this.pendingFiles.dataspaceCreated,
        workspaceName: selection.workspaceName,
        selectedTemplate: selection.selectedTemplate
      });
      this.pendingFiles = null;
    }
  }

  onSchemaSelectionCancelled() {
    this.showSchemaSelectionModal = false;
    this.pendingFiles = null;
  }

  onFileChange(event: any) {
    if (this.isGeneratingSchema) {
      return;
    }
    
    const files = event.target?.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files) as File[];
      const validFiles = this.validateFileSizes(fileArray);
      
      if (validFiles.length > 0) {
        this.pendingFiles = {
          files: validFiles,
          isDrop: false,
          dataspaceCreated: this.activeTable !== ''
        };
        this.showSchemaSelectionModal = true;
      }
    }
    
    if (event.target) {
      event.target.value = '';
    }
  }

  onDragEnter(event: DragEvent) {
    if (this.isGeneratingSchema) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    if (this.isGeneratingSchema) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    if (this.isGeneratingSchema) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent) {
    if (this.isGeneratingSchema) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      console.log('Empty state: Dropped files count:', fileArray.length, 'Files:', fileArray.map(f => f.name), 'activeTable:', this.activeTable);
      const validFiles = this.validateFileSizes(fileArray);
      
      if (validFiles.length > 0) {
        this.pendingFiles = {
          files: validFiles,
          isDrop: true,
          dataspaceCreated: this.activeTable !== ''
        };
        this.showSchemaSelectionModal = true;
      }
    }
  }


  toggleHelpMenu(): void {
    this.isHelpMenuOpen = !this.isHelpMenuOpen;
  }

  getHelp(): void {
    this.isHelpMenuOpen = false;
    window.location.href = 'mailto:info@talonic.ai?subject=I need help: support request';
  }

  talkToSales(): void {
    this.isHelpMenuOpen = false;
    window.open('https://www.talonic.com/get-started', '_blank');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const helpContainer = target.closest('.help-menu-container');
    
    if (!helpContainer && this.isHelpMenuOpen) {
      this.isHelpMenuOpen = false;
    }
  }

  private validateFileSizes(files: File[]): File[] {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(file);
      } else {
        validFiles.push(file);
      }
    }

    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.map(f => f.name).join(', ');
      const fileSizes = invalidFiles.map(f => (f.size / (1024 * 1024)).toFixed(1)).join(', ');
      this.toastService.showError(
        'File Size Error', 
        `The following files exceed the 50MB limit and were not uploaded: ${fileNames} (${fileSizes}MB each)`
      );
    }

    return validFiles;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
